/**
 * Status vocabularies, type guards, legal state transitions and human-readable labels for
 * applications and individual answers.
 *
 * The transition tables here are the single source of truth: candidate routes, admin routes and
 * the processor Worker all check a move against them before writing a new status to Firestore.
 * Labels are candidate- and HR-facing, so they never use the words this product refuses to use
 * ("interview", "screening", "assessment", "evaluation", "score").
 */

import type { AnswerStatus, ApplicationStatus } from './types';

/* ------------------------------------------------------------------------------------------------
 * Vocabularies
 * ---------------------------------------------------------------------------------------------- */

/** Every {@link ApplicationStatus}, in rough lifecycle order. */
export const APPLICATION_STATUSES = [
  'draft',
  'recording',
  'submitted',
  'transcribing',
  'summarizing',
  'ready',
  'error',
] as const satisfies readonly ApplicationStatus[];

/** Every {@link AnswerStatus}, in rough lifecycle order. */
export const ANSWER_STATUSES = [
  'draft',
  'recording',
  'uploading',
  'uploaded',
  'transcribing',
  'ready',
  'error',
] as const satisfies readonly AnswerStatus[];

/** Fails to compile unless `T` is exactly `true`; used for the exhaustiveness checks below. */
type AssertTrue<T extends true> = T;

/** Compile-time proof that {@link APPLICATION_STATUSES} lists every member of the union. */
type _AllApplicationStatusesListed = AssertTrue<
  [Exclude<ApplicationStatus, (typeof APPLICATION_STATUSES)[number]>] extends [never] ? true : false
>;

/** Compile-time proof that {@link ANSWER_STATUSES} lists every member of the union. */
type _AllAnswerStatusesListed = AssertTrue<
  [Exclude<AnswerStatus, (typeof ANSWER_STATUSES)[number]>] extends [never] ? true : false
>;

/* ------------------------------------------------------------------------------------------------
 * Type guards
 * ---------------------------------------------------------------------------------------------- */

/** Narrows an unknown value (e.g. a decoded Firestore field) to an {@link ApplicationStatus}. */
export function isApplicationStatus(value: unknown): value is ApplicationStatus {
  return (
    typeof value === 'string' && (APPLICATION_STATUSES as readonly string[]).includes(value)
  );
}

/** Narrows an unknown value (e.g. a decoded Firestore field) to an {@link AnswerStatus}. */
export function isAnswerStatus(value: unknown): value is AnswerStatus {
  return typeof value === 'string' && (ANSWER_STATUSES as readonly string[]).includes(value);
}

/* ------------------------------------------------------------------------------------------------
 * Classification
 * ---------------------------------------------------------------------------------------------- */

/**
 * True when an application has finished moving on its own: `ready` (fully processed) or `error`
 * (stopped, and only a human retry or reprocess will move it again).
 */
export function isTerminalApplicationStatus(status: ApplicationStatus): boolean {
  return status === 'ready' || status === 'error';
}

/**
 * True while an application is in the automated pipeline (`submitted`, `transcribing` or
 * `summarizing`). The cron sweep uses this to find work that stalled part-way through.
 */
export function isProcessingApplicationStatus(status: ApplicationStatus): boolean {
  return status === 'submitted' || status === 'transcribing' || status === 'summarizing';
}

/* ------------------------------------------------------------------------------------------------
 * Transition tables
 * ---------------------------------------------------------------------------------------------- */

/**
 * Legal next statuses for an application, keyed by current status. Every non-`ready` status may
 * move to `error`; `error` may be retried (`transcribing`) or fully reprocessed (`submitted`).
 */
export const APPLICATION_TRANSITIONS: Readonly<
  Record<ApplicationStatus, readonly ApplicationStatus[]>
> = {
  draft: ['draft', 'recording', 'error'],
  recording: ['recording', 'submitted', 'error'],
  submitted: ['transcribing', 'error'],
  transcribing: ['summarizing', 'error'],
  summarizing: ['ready', 'error'],
  ready: [],
  error: ['submitted', 'transcribing', 'error'],
};

/**
 * Legal next statuses for a single answer, keyed by current status. A retake rewinds
 * `uploading` → `draft` or `uploaded` → `uploading`, and any status may move to `error`.
 */
export const ANSWER_TRANSITIONS: Readonly<Record<AnswerStatus, readonly AnswerStatus[]>> = {
  draft: ['recording', 'error'],
  recording: ['uploading', 'error'],
  uploading: ['draft', 'uploaded', 'error'],
  uploaded: ['uploading', 'transcribing', 'error'],
  transcribing: ['ready', 'error'],
  ready: ['error'],
  error: ['uploading', 'transcribing', 'error'],
};

/**
 * True when an application may move from `from` to `to`. Unknown status strings are rejected, so
 * this is safe to call on values decoded from Firestore.
 */
export function canTransitionApplication(from: ApplicationStatus, to: ApplicationStatus): boolean {
  if (!isApplicationStatus(from) || !isApplicationStatus(to)) return false;
  return APPLICATION_TRANSITIONS[from].includes(to);
}

/**
 * True when an answer may move from `from` to `to`. Unknown status strings are rejected, so this
 * is safe to call on values decoded from Firestore.
 */
export function canTransitionAnswer(from: AnswerStatus, to: AnswerStatus): boolean {
  if (!isAnswerStatus(from) || !isAnswerStatus(to)) return false;
  return ANSWER_TRANSITIONS[from].includes(to);
}

/* ------------------------------------------------------------------------------------------------
 * Labels
 * ---------------------------------------------------------------------------------------------- */

/** Candidate- and HR-facing wording for each {@link ApplicationStatus}. */
const APPLICATION_STATUS_LABELS: Readonly<Record<ApplicationStatus, string>> = {
  draft: 'Started',
  recording: 'Recording answers',
  submitted: 'Submitted',
  transcribing: 'Preparing transcript',
  summarizing: 'Preparing summary',
  ready: 'Ready',
  error: 'Needs attention',
};

/** Candidate- and HR-facing wording for each {@link AnswerStatus}. */
const ANSWER_STATUS_LABELS: Readonly<Record<AnswerStatus, string>> = {
  draft: 'Not recorded yet',
  recording: 'Recording',
  uploading: 'Uploading',
  uploaded: 'Uploaded',
  transcribing: 'Preparing transcript',
  ready: 'Ready',
  error: 'Needs attention',
};

/**
 * Human wording for an application status, e.g. `'summarizing'` → `"Preparing summary"`.
 * Unrecognised values fall back to `"Unknown"` rather than leaking a raw enum string.
 */
export function applicationStatusLabel(status: ApplicationStatus): string {
  return isApplicationStatus(status) ? APPLICATION_STATUS_LABELS[status] : 'Unknown';
}

/**
 * Human wording for an answer status, e.g. `'uploaded'` → `"Uploaded"`. Unrecognised values fall
 * back to `"Unknown"` rather than leaking a raw enum string.
 */
export function answerStatusLabel(status: AnswerStatus): string {
  return isAnswerStatus(status) ? ANSWER_STATUS_LABELS[status] : 'Unknown';
}
